import { useState, useRef, ChangeEvent } from "react";
import { Button } from "@/components/ui/button";
import { notify } from "@/hooks/useNotify";
import { Camera, User, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

interface ProfilePhotoUploadProps {
  onImageUpload: (image: string | null) => void;
  initialImage?: string;
  className?: string;
  userId?: string;
}

const ProfilePhotoUpload = ({
  onImageUpload,
  initialImage,
  className = "",
  userId,
}: ProfilePhotoUploadProps) => {
  const [previewUrl, setPreviewUrl] = useState<string | null>(initialImage || null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleImageChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];
    if (!validTypes.includes(file.type)) {
      notify.error("Please upload an image file (JPEG, PNG, GIF, or WebP).");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      notify.error("Image size should be less than 5MB");
      return;
    }

    if (userId) {
      try {
        const ext = file.type.split('/')[1]?.replace('jpeg', 'jpg') ?? 'jpg';
        const path = `${userId}/avatar.${ext}`;

        const { error: uploadError } = await supabase.storage
          .from('profile-photos')
          .upload(path, file, { upsert: true, contentType: file.type });

        if (uploadError) throw uploadError;

        const { data } = supabase.storage
          .from('profile-photos')
          .getPublicUrl(path);

        setPreviewUrl(data.publicUrl);
        onImageUpload(data.publicUrl);
      } catch (err) {
        notify.error("Failed to upload photo. Please try again.");
        console.error('Photo upload error:', err);
      }
    } else {
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result as string;
        setPreviewUrl(result);
        onImageUpload(result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setPreviewUrl(null);
    onImageUpload(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <div className={`flex flex-col items-center gap-4 ${className}`}>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleImageChange}
        accept="image/jpeg, image/png, image/gif, image/webp"
        className="sr-only"
        aria-label="Upload profile photo"
      />
      
      {previewUrl ? (
        <div className="relative w-32 h-32 rounded-full overflow-hidden border border-gray-200">
          <img
            src={previewUrl}
            alt="Profile photo preview"
            className="w-full h-full object-cover"
          />
          <button
            type="button"
            onClick={handleRemoveImage}
            className="absolute top-0 right-0 bg-white rounded-full p-1 shadow-md hover:bg-gray-100"
            aria-label="Remove photo"
          >
            <X size={16} className="text-gray-700" />
          </button>
        </div>
      ) : (
        <div className="w-32 h-32 border-2 border-dashed border-gray-300 rounded-full flex flex-col items-center justify-center bg-gray-50">
          <User size={32} className="text-gray-400 mb-1" />
          <p className="text-xs text-gray-500 text-center">Profile photo</p>
        </div>
      )}
      
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => fileInputRef.current?.click()}
        className="flex items-center gap-1"
      >
        <Camera size={16} />
        {previewUrl ? "Change Photo" : "Upload Photo"}
      </Button>
    </div>
  );
};

export default ProfilePhotoUpload;
